import type { EditAppointmentType } from 'RestAPIs/activities/types';
import { createContext } from 'react';

/**
 * The confirmation-popover callbacks a view hands to the cards and column
 * headers below it.
 *
 * `onClose` and `onHandleAppointmentConfirmation` may only be called from a user
 * event or an async callback — never during render, and never from a layout
 * effect. `CardsViewPragmatic` supplies them as ref-latched trampolines with
 * permanently stable identities: every card and column header consumes this
 * context, and context propagation ignores memoization, so a value that changes
 * identity re-renders the whole board. It assigns those refs in a
 * `useLayoutEffect`, and a child's layout effect runs *before* its parent's — so
 * a call from one would reach the previous render's closure, and on first mount
 * the initial no-op. A consumer cannot tell which view provided its value (the
 * other views pass plain inline closures today, which is not something to rely
 * on), so the rule holds for all of them.
 *
 * Why *permanently* stable and not merely correctly invalidated: compiled
 * straight into the value, `onClose` invalidates on `shouldShowPopoverDialog` and
 * `onHandleAppointmentConfirmation` on eight values including `planningEntities`
 * — a board-wide re-render inside the frame that has to show the drop feedback.
 * The ref indirection is what earns the stability, not a wrapper around it: a
 * closure reading only a ref has no reactive dependencies, so the React Compiler
 * parks it at the memo-cache sentinel — cached once, never invalidated. Hence no
 * `useCallback` on the trampolines and no `useMemo` on the value; compiling
 * `CardsViewPragmatic` with and without them gives byte-identical output bar the
 * dropped import. `CardsViewPragmatic.memoization.spec.ts` pins this.
 *
 * `isLoading` is exempt from the call-time rule: a plain value, safe to read
 * anywhere. It is also the one field still invalidating this context, twice per
 * drop — both times after the feedback has painted, which is why it is
 * acceptable. The end-state is the `useIsAnyDragActive(enabled)` treatment, an
 * opt-in subscription, since only the one card whose popover is open can act on
 * it; that changes the shape of a context five other views share, so it is left
 * for separate work.
 */
export const PopOverConfirmationContext = createContext<{
  onClose: (() => void) | undefined;
  onHandleAppointmentConfirmation:
    | ((
        editAppointmentType: EditAppointmentType,
        isUnassignment?: boolean
      ) => void)
    | undefined;
  isLoading: boolean;
}>({
  onClose: undefined,
  onHandleAppointmentConfirmation: undefined,
  isLoading: false
});
